import {
  isOrgBranchServiceVariant,
  isTenantOrganizationServiceVariant,
} from '@/lib/service-variant-types'

type UnknownRecord = Record<string, unknown>

export const signupTemplateKeys = ['organization', 'user', 'vendor', 'default'] as const
export type SignupTemplateKey = (typeof signupTemplateKeys)[number]

export const templateChannels = ['emails', 'sms', 'push'] as const
export type TemplateChannel = (typeof templateChannels)[number]

export type TemplateScope = 'organization' | 'tenant' | 'branch' | 'all'

/** Where the templates UI is opened: drives which built-in / scoped templates are listed. */
export type TemplateEditorContext = 'organization' | 'tenant' | 'branch'

export type EmailTemplateDraft = {
  subject: string
  heading: string
  intro: string
}

export type SmsTemplateDraft = {
  message: string
}

export type PushTemplateDraft = {
  title: string
  message: string
}

export type TemplateMeta = {
  /** Stable key within purpose+channel (slug), e.g. retail_v1 — not always the config storage id. */
  template_key?: string
  template_label?: string
  template_scope?: TemplateScope
  /** Business grouping: signup, order_confirmation, etc. */
  template_purpose?: string
}

export type TemplateEntryEmail = EmailTemplateDraft & TemplateMeta
export type TemplateEntrySms = SmsTemplateDraft & TemplateMeta
export type TemplateEntryPush = PushTemplateDraft & TemplateMeta

export type ServiceInstanceSignupTemplatesState = {
  emails: Record<string, TemplateEntryEmail>
  sms: Record<string, TemplateEntrySms>
  push: Record<string, TemplateEntryPush>
}

export const signupTemplateDefinitions: Array<{
  key: SignupTemplateKey
  label: string
  description: string
  scope: TemplateScope
}> = [
  {
    key: 'organization',
    label: 'Organization Signup',
    description: 'Used when a new organization or tenant owner completes signup.',
    scope: 'organization',
  },
  {
    key: 'user',
    label: 'User Signup',
    description: 'Used when a standard user signs up under an existing organization or tenant.',
    scope: 'tenant',
  },
  {
    key: 'vendor',
    label: 'Vendor Signup',
    description: 'Used when a vendor branch signup flow creates a new vendor-owned branch.',
    scope: 'branch',
  },
  {
    key: 'default',
    label: 'Default Fallback',
    description: 'Used when a specific signup template is not configured for the selected flow.',
    scope: 'all',
  },
]

export const templateChannelDefinitions: Array<{
  key: TemplateChannel
  label: string
  description: string
}> = [
  {
    key: 'emails',
    label: 'Emails',
    description: 'Rich email templates with separate subject, heading, and intro content.',
  },
  {
    key: 'sms',
    label: 'Sms',
    description: 'Short text message templates for signup notifications.',
  },
  {
    key: 'push',
    label: 'Push Notifications',
    description: 'Push notification title and message templates for signup events.',
  },
]

export const signupTemplatePlaceholderTokens = [
  '{recipient_name}',
  '{owner_name}',
  '{organization_name}',
  '{service_name}',
  '{service_branch_name}',
  '{service_instance_id}',
  '{status}',
  '{signup_label}',
  '{otp_code}',
  '{otp_length}',
] as const

const notificationConfigPathByChannel: Record<TemplateChannel, string> = {
  emails: 'signup_email_templates',
  sms: 'signup_sms_templates',
  push: 'signup_push_templates',
}

const BUILTIN_SCOPE_BY_KEY: Record<SignupTemplateKey, TemplateScope> = {
  organization: 'organization',
  user: 'tenant',
  vendor: 'branch',
  default: 'all',
}

export const DEFAULT_TEMPLATE_PURPOSE = 'signup' as const

const TEMPLATE_PURPOSE_PATTERN = /^[a-z][a-z0-9_]{0,31}$/

export function isValidTemplatePurpose(value: string): boolean {
  return TEMPLATE_PURPOSE_PATTERN.test(value.trim().toLowerCase())
}

export function parseTemplateStorageId(storageId: string): {
  purpose: string
  templateKey: string
  builtin: boolean
} {
  const raw = String(storageId ?? '').trim()
  if (isBuiltinSignupTemplateKey(raw)) {
    return { purpose: DEFAULT_TEMPLATE_PURPOSE, templateKey: raw, builtin: true }
  }
  const idx = raw.indexOf(':')
  if (idx !== -1) {
    return {
      purpose: raw.slice(0, idx).trim().toLowerCase(),
      templateKey: raw.slice(idx + 1).trim().toLowerCase(),
      builtin: false,
    }
  }
  return {
    purpose: DEFAULT_TEMPLATE_PURPOSE,
    templateKey: normalizeCustomTemplateSlug(raw),
    builtin: false,
  }
}

export function encodeCustomTemplateStorageId(purpose: string, templateKey: string): string {
  const p = purpose.trim().toLowerCase()
  const k = normalizeCustomTemplateSlug(templateKey)
  return `${p}:${k}`
}

export function isCustomTemplateStorageId(storageId: string): boolean {
  return !parseTemplateStorageId(storageId).builtin
}

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function cloneConfigValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => cloneConfigValue(item)) as T
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneConfigValue(item)]),
    ) as T
  }

  return value
}

function emptyEmailTemplate(): TemplateEntryEmail {
  return {
    subject: '',
    heading: '',
    intro: '',
  }
}

function emptySmsTemplate(): TemplateEntrySms {
  return {
    message: '',
  }
}

function emptyPushTemplate(): TemplateEntryPush {
  return {
    title: '',
    message: '',
  }
}

function normalizeStringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function isBuiltinSignupTemplateKey(key: string): key is SignupTemplateKey {
  return (signupTemplateKeys as readonly string[]).includes(key)
}

export function resolveSignupTemplateEditorContext(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): TemplateEditorContext {
  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return 'branch'
  }
  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return 'tenant'
  }
  return 'organization'
}

function parseScope(value: unknown): TemplateScope | undefined {
  if (value === 'organization' || value === 'tenant' || value === 'branch' || value === 'all') {
    return value
  }
  return undefined
}

function readMetaFromRecord(raw: unknown): TemplateMeta {
  if (!isRecord(raw)) {
    return {}
  }
  const template_key = typeof raw.template_key === 'string' ? raw.template_key.trim() : undefined
  const template_label = typeof raw.template_label === 'string' ? raw.template_label.trim() : undefined
  const template_purpose = typeof raw.template_purpose === 'string' ? raw.template_purpose.trim().toLowerCase() : undefined
  const template_scope = parseScope(raw.template_scope)
  return {
    ...(template_key ? { template_key } : {}),
    ...(template_label ? { template_label } : {}),
    ...(template_purpose ? { template_purpose } : {}),
    ...(template_scope ? { template_scope } : {}),
  }
}

function mergeMeta(...metas: TemplateMeta[]): TemplateMeta {
  return metas.reduce<TemplateMeta>((acc, m) => ({ ...acc, ...m }), {})
}

export function resolveEffectiveTemplateScope(
  storageId: string,
  entry: TemplateMeta,
): TemplateScope {
  if (entry.template_scope) {
    return entry.template_scope
  }
  const parsed = parseTemplateStorageId(storageId)
  if (parsed.builtin) {
    return BUILTIN_SCOPE_BY_KEY[parsed.templateKey as SignupTemplateKey]
  }
  return 'organization'
}

/**
 * Branch workspace: only `branch` and `all` scoped templates.
 * Organization / tenant: all scopes (org admins configure branch-scoped mail too).
 */
export function isSignupTemplateVisibleInContext(
  storageId: string,
  entry: TemplateMeta,
  context: TemplateEditorContext,
): boolean {
  const scope = resolveEffectiveTemplateScope(storageId, entry)
  if (context === 'branch') {
    return scope === 'branch' || scope === 'all'
  }
  return (
    scope === 'organization'
    || scope === 'tenant'
    || scope === 'branch'
    || scope === 'all'
  )
}

export function collectDistinctTemplateKeys(state: ServiceInstanceSignupTemplatesState): string[] {
  const keys = new Set<string>()
  Object.keys(state.emails).forEach((k) => keys.add(k))
  Object.keys(state.sms).forEach((k) => keys.add(k))
  Object.keys(state.push).forEach((k) => keys.add(k))
  signupTemplateKeys.forEach((k) => keys.add(k))
  return Array.from(keys).sort((a, b) => {
    const order = (k: string) => {
      const idx = (signupTemplateKeys as readonly string[]).indexOf(k)
      return idx === -1 ? 100 + k.charCodeAt(0) : idx
    }
    return order(a) - order(b) || a.localeCompare(b)
  })
}

const TEMPLATE_SLUG_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/

export function isValidCustomTemplateSlug(value: string): boolean {
  return TEMPLATE_SLUG_PATTERN.test(value.trim().toLowerCase())
}

export function normalizeCustomTemplateSlug(value: string): string {
  return value.trim().toLowerCase()
}

export function assertUniqueNewTemplatePurposeKey(
  purpose: string,
  templateKey: string,
  state: ServiceInstanceSignupTemplatesState,
): boolean {
  const p = purpose.trim().toLowerCase()
  const k = normalizeCustomTemplateSlug(templateKey)
  if (!k || isBuiltinSignupTemplateKey(k) || !isValidTemplatePurpose(p)) {
    return false
  }
  const composite = encodeCustomTemplateStorageId(p, k)
  const keys = collectDistinctTemplateKeys(state)
  if (keys.includes(composite)) {
    return false
  }
  if (p === DEFAULT_TEMPLATE_PURPOSE && keys.includes(k) && !isBuiltinSignupTemplateKey(k)) {
    return false
  }
  return true
}

export function assertUniqueNewTemplateKey(
  candidate: string,
  state: ServiceInstanceSignupTemplatesState,
): boolean {
  return assertUniqueNewTemplatePurposeKey(DEFAULT_TEMPLATE_PURPOSE, candidate, state)
}

export function addCustomTemplateToState(
  state: ServiceInstanceSignupTemplatesState,
  purpose: string,
  templateKey: string,
  templateLabel: string,
  scope: TemplateScope,
  channels: readonly TemplateChannel[] = templateChannels,
): ServiceInstanceSignupTemplatesState {
  const p = purpose.trim().toLowerCase()
  const key = normalizeCustomTemplateSlug(templateKey)
  const storageId = encodeCustomTemplateStorageId(p, key)
  const label = templateLabel.trim() || key
  const meta: TemplateMeta = {
    template_key: key,
    template_purpose: p,
    template_label: label,
    template_scope: scope,
  }
  const useChannel = new Set<TemplateChannel>(channels)
  return {
    emails: useChannel.has('emails')
      ? {
          ...state.emails,
          [storageId]: {
            ...emptyEmailTemplate(),
            ...meta,
          },
        }
      : state.emails,
    sms: useChannel.has('sms')
      ? {
          ...state.sms,
          [storageId]: {
            ...emptySmsTemplate(),
            ...meta,
          },
        }
      : state.sms,
    push: useChannel.has('push')
      ? {
          ...state.push,
          [storageId]: {
            ...emptyPushTemplate(),
            ...meta,
          },
        }
      : state.push,
  }
}

export function removeTemplateStorageIdFromState(
  state: ServiceInstanceSignupTemplatesState,
  storageId: string,
): ServiceInstanceSignupTemplatesState {
  if (!isCustomTemplateStorageId(storageId)) {
    return state
  }
  const emails = { ...state.emails }
  const sms = { ...state.sms }
  const push = { ...state.push }
  delete emails[storageId]
  delete sms[storageId]
  delete push[storageId]
  return { emails, sms, push }
}

function getNotificationSection(config: unknown, channel: TemplateChannel): UnknownRecord {
  if (!isRecord(config)) {
    return {}
  }

  const notifications = config.notifications
  if (!isRecord(notifications)) {
    return {}
  }

  const section = notifications[notificationConfigPathByChannel[channel]]
  return isRecord(section) ? section : {}
}

function parseEmailEntry(key: string, raw: unknown, metaFallback: TemplateMeta): TemplateEntryEmail {
  const fromRecord = isRecord(raw) ? raw : {}
  const parsed = parseTemplateStorageId(key)
  const meta = mergeMeta(metaFallback, readMetaFromRecord(fromRecord))
  if (!meta.template_purpose) {
    meta.template_purpose = parsed.purpose
  }
  if (!meta.template_key) {
    meta.template_key = parsed.templateKey
  }
  if (!parsed.builtin && !meta.template_label) {
    meta.template_label = parsed.templateKey
  }
  return {
    subject: normalizeStringValue(fromRecord.subject),
    heading: normalizeStringValue(fromRecord.heading),
    intro: normalizeStringValue(fromRecord.intro),
    ...meta,
  }
}

function parseSmsEntry(key: string, raw: unknown, metaFallback: TemplateMeta): TemplateEntrySms {
  const fromRecord = isRecord(raw) ? raw : {}
  const parsed = parseTemplateStorageId(key)
  const meta = mergeMeta(metaFallback, readMetaFromRecord(fromRecord))
  if (!meta.template_purpose) {
    meta.template_purpose = parsed.purpose
  }
  if (!meta.template_key) {
    meta.template_key = parsed.templateKey
  }
  if (!parsed.builtin && !meta.template_label) {
    meta.template_label = parsed.templateKey
  }
  return {
    message: normalizeStringValue(fromRecord.message),
    ...meta,
  }
}

function parsePushEntry(key: string, raw: unknown, metaFallback: TemplateMeta): TemplateEntryPush {
  const fromRecord = isRecord(raw) ? raw : {}
  const parsed = parseTemplateStorageId(key)
  const meta = mergeMeta(metaFallback, readMetaFromRecord(fromRecord))
  if (!meta.template_purpose) {
    meta.template_purpose = parsed.purpose
  }
  if (!meta.template_key) {
    meta.template_key = parsed.templateKey
  }
  if (!parsed.builtin && !meta.template_label) {
    meta.template_label = parsed.templateKey
  }
  return {
    title: normalizeStringValue(fromRecord.title),
    message: normalizeStringValue(fromRecord.message),
    ...meta,
  }
}

function collectAllKeysFromConfig(config: unknown): Set<string> {
  const keys = new Set<string>()
  templateChannels.forEach((channel) => {
    Object.keys(getNotificationSection(config, channel)).forEach((k) => keys.add(k))
  })
  signupTemplateKeys.forEach((k) => keys.add(k))
  return keys
}

export function parseServiceInstanceSignupTemplates(config: unknown): ServiceInstanceSignupTemplatesState {
  const emailSection = getNotificationSection(config, 'emails')
  const smsSection = getNotificationSection(config, 'sms')
  const pushSection = getNotificationSection(config, 'push')

  const allKeys = collectAllKeysFromConfig(config)

  const emails: Record<string, TemplateEntryEmail> = {}
  const sms: Record<string, TemplateEntrySms> = {}
  const push: Record<string, TemplateEntryPush> = {}

  for (const key of allKeys) {
    const metaFallback = mergeMeta(
      readMetaFromRecord(emailSection[key]),
      readMetaFromRecord(smsSection[key]),
      readMetaFromRecord(pushSection[key]),
    )
    emails[key] = parseEmailEntry(key, emailSection[key], metaFallback)
    sms[key] = parseSmsEntry(key, smsSection[key], metaFallback)
    push[key] = parsePushEntry(key, pushSection[key], metaFallback)
  }

  return { emails, sms, push }
}

function isPersistedCustomTemplateMeta(template: TemplateMeta): boolean {
  return Boolean(
    (template.template_key && template.template_scope)
    || (template.template_purpose && template.template_key),
  )
}

function cleanEmailTemplate(template: TemplateEntryEmail): UnknownRecord | null {
  const subject = template.subject.trim()
  const heading = template.heading.trim()
  const intro = template.intro.trim()
  const cleanedTemplate: UnknownRecord = {}

  if (subject) {
    cleanedTemplate.subject = subject
  }
  if (heading) {
    cleanedTemplate.heading = heading
  }
  if (intro) {
    cleanedTemplate.intro = intro
  }

  const isCustom = isPersistedCustomTemplateMeta(template)
  if (isCustom) {
    cleanedTemplate.template_key = template.template_key
    cleanedTemplate.template_label = (template.template_label || template.template_key || '').trim()
    cleanedTemplate.template_scope = template.template_scope
    if (template.template_purpose) {
      cleanedTemplate.template_purpose = template.template_purpose
    }
  }

  return Object.keys(cleanedTemplate).length > 0 ? cleanedTemplate : null
}

function cleanSmsTemplate(template: TemplateEntrySms): UnknownRecord | null {
  const message = template.message.trim()
  const cleanedTemplate: UnknownRecord = {}
  if (message) {
    cleanedTemplate.message = message
  }
  const isCustom = isPersistedCustomTemplateMeta(template)
  if (isCustom) {
    cleanedTemplate.template_key = template.template_key
    cleanedTemplate.template_label = (template.template_label || template.template_key || '').trim()
    cleanedTemplate.template_scope = template.template_scope
    if (template.template_purpose) {
      cleanedTemplate.template_purpose = template.template_purpose
    }
  }
  return Object.keys(cleanedTemplate).length > 0 ? cleanedTemplate : null
}

function cleanPushTemplate(template: TemplateEntryPush): UnknownRecord | null {
  const title = template.title.trim()
  const message = template.message.trim()
  const cleanedTemplate: UnknownRecord = {}

  if (title) {
    cleanedTemplate.title = title
  }
  if (message) {
    cleanedTemplate.message = message
  }
  const isCustom = isPersistedCustomTemplateMeta(template)
  if (isCustom) {
    cleanedTemplate.template_key = template.template_key
    cleanedTemplate.template_label = (template.template_label || template.template_key || '').trim()
    cleanedTemplate.template_scope = template.template_scope
    if (template.template_purpose) {
      cleanedTemplate.template_purpose = template.template_purpose
    }
  }
  return Object.keys(cleanedTemplate).length > 0 ? cleanedTemplate : null
}

export function mergeServiceInstanceSignupTemplates(
  currentConfig: unknown,
  nextTemplates: ServiceInstanceSignupTemplatesState,
): UnknownRecord {
  const nextConfig = isRecord(currentConfig) ? cloneConfigValue(currentConfig) : {}
  const notifications = isRecord(nextConfig.notifications)
    ? cloneConfigValue(nextConfig.notifications)
    : {}

  const allKeys = collectDistinctTemplateKeys(nextTemplates)

  templateChannels.forEach((channel) => {
    const sectionKey = notificationConfigPathByChannel[channel]
    const existingSection = isRecord(notifications[sectionKey])
      ? cloneConfigValue(notifications[sectionKey])
      : {}

    allKeys.forEach((templateKey) => {
      const cleanedTemplate = channel === 'emails'
        ? cleanEmailTemplate(nextTemplates.emails[templateKey] ?? emptyEmailTemplate())
        : channel === 'sms'
          ? cleanSmsTemplate(nextTemplates.sms[templateKey] ?? emptySmsTemplate())
          : cleanPushTemplate(nextTemplates.push[templateKey] ?? emptyPushTemplate())

      if (cleanedTemplate) {
        existingSection[templateKey] = cleanedTemplate
      } else {
        delete existingSection[templateKey]
      }
    })

    if (Object.keys(existingSection).length > 0) {
      notifications[sectionKey] = existingSection
    } else {
      delete notifications[sectionKey]
    }
  })

  if (Object.keys(notifications).length > 0) {
    nextConfig.notifications = notifications
  } else {
    delete nextConfig.notifications
  }

  return nextConfig
}

const CONTENT_FIELD_DENYLIST = new Set([
  'template_key',
  'template_label',
  'template_scope',
  'template_purpose',
])

export function isSignupTemplateConfigured(
  template: EmailTemplateDraft | SmsTemplateDraft | PushTemplateDraft,
): boolean {
  return Object.entries(template).some(
    ([key, value]) => !CONTENT_FIELD_DENYLIST.has(key) && String(value ?? '').trim().length > 0,
  )
}
